import type { MatchState, PlayerState } from '../sim/state';
import type { SimConfig } from '../config/simConfig';
import type { SimEvent, FoulOutcome } from '../sim/events';
import type { ContactEvent } from '../physics/collide';
import type { EntityId, TeamIdx, Vec2, ActionKind, CardKind } from '../types';
import { PITCH } from '../config/pitchConfig';
import { rngFloat } from '../math/rng';
import { foulSeverity, classifyFoul, type FoulVerdict } from './foul';
import { refClarity, pDetect } from './detect';
import { startRestart, startPenalty } from '../rules/phase';
import { moveReferee } from './movement';
import { judgeDive } from './dive';

// refereeStep (§7) — step 7 of the pipeline. RNG discipline (normative): foul-candidate detection rolls in
// ascending offender id (victim id tiebreak), then dive judgments (§7.4, later), then the ref scan-timer
// draw. Pure apart from state.rng. Detected fouls whistle → restart + cards; ref-abuse is an instant red
// with no roll. (Advantage/diving layer in atop this.)

const AGGRESSIVE: ReadonlySet<ActionKind> = new Set<ActionKind>(['SHOVE', 'PUNCH_WINDUP', 'PUNCH_ACTIVE', 'TACKLE_STAND', 'TACKLE_SLIDE']);

function actionRank(a: ActionKind, cfg: SimConfig): number {
  const w = cfg.referee.actionW;
  if (a === 'PUNCH_ACTIVE') return w.PUNCH_ACTIVE;
  if (a === 'SHOVE') return w.SHOVE;
  if (a === 'TACKLE_SLIDE') return w.TACKLE_SLIDE;
  if (a === 'TACKLE_STAND') return w.TACKLE_STAND;
  return w.OTHER;
}

/** Is `spot` inside `team`'s own penalty box (its defensive box, where a foul ⇒ penalty for the opponents)? */
function spotInOwnBox(state: MatchState, spot: Vec2, team: TeamIdx): boolean {
  const pitch = PITCH[state.mode.teamSize];
  const dir = state.attackDir[team];
  const ownGoalX = -dir * (pitch.length / 2);
  const xFromGoal = dir * (spot.x - ownGoalX);
  return xFromGoal >= 0 && xFromGoal <= pitch.penaltyBoxDepth && Math.abs(spot.y) <= pitch.penaltyBoxHalfWidth;
}

/** Pass isGk to the lowest-id remaining teammate when a keeper is sent off (P19, §7.6). */
function reassignGk(state: MatchState, gk: PlayerState): void {
  if (!gk.isGk || state.mode.teamSize < 3) return;
  gk.isGk = false;
  const heir = state.players.filter((p) => p.team === gk.team && !p.sentOff && p.id !== gk.id).sort((a, b) => a.id - b.id)[0];
  if (heir) heir.isGk = true;
}

/** Apply a card to `entity` (§7.6): a 2nd yellow becomes a red; a red sends the player off — for a TIMED
 *  suspension now (D65c), not permanently. Emits CARD. */
function applyCard(state: MatchState, entity: EntityId, verdict: 'yellow' | 'red', reason: 'FOUL' | 'SIMULATION' | 'VIOLENT' | 'REF_ABUSE', cfg: SimConfig, events: SimEvent[]): void {
  const p = state.players[entity]!;
  const disc = state.discipline.perPlayer[entity]!;
  if (verdict === 'yellow') {
    disc.yellows = Math.min(2, disc.yellows + 1) as 0 | 1 | 2;
    p.card = 'YELLOW';
    events.push({ t: 'CARD', entity, card: 'YELLOW', reason });
    if (disc.yellows < 2) return;
  }
  // red (straight or second yellow)
  const secondYellow = verdict === 'yellow';
  disc.red = true;
  p.card = 'RED';
  p.sentOff = true;
  // D65c: a red is a TIMED suspension — sentOff keeps every "is he on the pitch" check working, and
  // resolveSuspensions returns him after SUSPENSION_TICKS (20 s). A second red re-suspends the same way.
  p.suspendedUntilTick = state.tick + cfg.referee.SUSPENSION_TICKS;
  const pitch = PITCH[state.mode.teamSize];
  // D62: SPACE the bench and make the player inert. Every sent-off player used to be parked on the
  // identical point, where resolvePlayerCollisions (which does not filter sentOff) de-penetrated them
  // against each other every tick — and applyActions skips sentOff players, so their `action` stayed
  // frozen at whatever aggressive verb they were carded in. The referee therefore kept detecting an
  // AGGRESSIVE contact between two benched men and awarding free kicks at their bench position, ~3.5 m
  // OUTSIDE the wall where neither taker nor ball can ever reach: an endless play-2-s / freeze-10-s loop.
  const benched = state.players.filter((o) => o.sentOff && o.id !== p.id).length;
  p.pos = { x: -pitch.length * 0.3 + benched * 1.6, y: -pitch.width / 2 - 3.5 }; // D55: clearly OFF the pitch
  p.vel = { x: 0, y: 0 };
  p.action = 'IDLE'; // never leave him frozen mid-tackle — that verb is what the referee reads
  p.actionTicks = 0;
  p.charge = null;
  reassignGk(state, p);
  // D62: if he was the designated restart taker, hand the restart to someone who can actually take it.
  // Cards are brandished on non-PLAY ticks — i.e. exactly during RESTART_PENDING — and possession.ts
  // blocks every player except `restart.taker` from claiming a dead ball, while applyKicks refuses any
  // other kicker: sending the taker off left the restart physically untakeable for its whole window.
  const r = state.restart;
  if (r && r.taker === entity) {
    let heir: PlayerState | null = null;
    let bestD = Infinity;
    for (const o of state.players) {
      if (o.team !== r.team || o.sentOff || o.id === entity) continue;
      const d = Math.hypot(o.pos.x - r.spot.x, o.pos.y - r.spot.y);
      if (d < bestD || (d === bestD && heir !== null && o.id < heir.id)) {
        bestD = d;
        heir = o;
      }
    }
    r.taker = heir ? heir.id : null;
  }
  events.push({ t: 'CARD', entity, card: 'RED' as CardKind, reason: secondYellow ? 'SECOND_YELLOW' : reason });
}

/** Brandish every queued card at a stoppage (§7.6) — routed through applyCard so 2nd-yellow→red still fires. */
function brandishQueuedCards(state: MatchState, cfg: SimConfig, events: SimEvent[]): void {
  if (state.cardQueue.length === 0) return;
  const queue = state.cardQueue.slice();
  state.cardQueue.length = 0;
  for (const q of queue) {
    const p = state.players[q.entity];
    if (!p || p.sentOff) continue; // already off ⇒ nothing to brandish
    const reason = q.reason === 'SECOND_YELLOW' ? 'FOUL' : q.reason;
    applyCard(state, q.entity, q.card === 'RED' ? 'red' : 'yellow', reason, cfg, events);
  }
}

interface Candidate {
  offender: EntityId;
  victim: EntityId;
  severity: number;
  verdict: FoulVerdict;
  spot: Vec2;
}

function cardFor(verdict: FoulVerdict): 'yellow' | 'red' | null {
  return verdict === 'yellow' ? 'yellow' : verdict === 'red' ? 'red' : null;
}

function possessorTeamOf(state: MatchState): TeamIdx | null {
  return state.ball.possessor !== null ? state.players[state.ball.possessor]?.team ?? null : null;
}

/**
 * Resolve a held advantage (§7.3). MATERIALIZED if the victim team keeps possession ≥ ADV_KEEP, shoots, or
 * scores (a goal in the window always stands); RECALLED if possession is lost to the offender team or the
 * ball dies any other way; at expiry, contested ⇒ MATERIALIZED iff the victim team is in possession. A
 * recall reinstates the original free kick; the card queues (materialized) or is applied now (recalled).
 */
function resolveAdvantage(state: MatchState, cfg: SimConfig, events: SimEvent[]): void {
  const adv = state.advantage;
  if (!adv) return;
  adv.ticksLeft -= 1;
  const elapsed = cfg.referee.ADV_WINDOW - adv.ticksLeft;
  const possTeam = possessorTeamOf(state);
  const victimInPoss = possTeam === adv.victimTeam;
  const shotByVictim = events.some((e) => e.t === 'SHOT' && state.players[e.entity]?.team === adv.victimTeam);
  const goal = state.phase === 'GOAL_CELEBRATION';
  // D62: a half/full-time whistle ENDS the advantage — it does not recall it into a free kick. The
  // RECALLED branch calls startRestart, which setPhase(RESTART_PENDING) and so clobbered the HALFTIME
  // that endHalf had just set. advancePhase's HALFTIME branch is the ONLY place `half` is incremented
  // and `clockTicks` reset, so it never ran: the sides had already swapped, the half never advanced,
  // and checkHalfEnd whistled HALF_END again — a second swap, a pinned clock, a match stuck in half 1.
  if (state.phase === 'HALFTIME' || state.phase === 'FULLTIME') {
    events.push({ t: 'ADVANTAGE', state: 'RECALLED' }); // the whistle has gone; nothing to restart into
    state.advantage = null;
    return;
  }
  const deadOther = state.phase !== 'PLAY' && !goal;

  let outcome: 'MATERIALIZED' | 'RECALLED' | null = null;
  if (goal || shotByVictim) outcome = 'MATERIALIZED';
  else if (deadOther) outcome = 'RECALLED';
  else if (possTeam !== null && possTeam !== adv.victimTeam) outcome = 'RECALLED'; // stolen by the offender team
  else if (elapsed >= cfg.referee.ADV_KEEP && victimInPoss) outcome = 'MATERIALIZED';
  else if (adv.ticksLeft <= 0) outcome = victimInPoss ? 'MATERIALIZED' : 'RECALLED';
  if (!outcome) return; // keep holding

  const card = cardFor(classifyFoul(adv.severity, cfg));
  if (outcome === 'MATERIALIZED') {
    if (card) state.cardQueue.push({ entity: adv.offender, card: card === 'red' ? 'RED' : 'YELLOW', reason: 'FOUL' });
    events.push({ t: 'ADVANTAGE', state: 'MATERIALIZED' });
  } else {
    events.push({ t: 'ADVANTAGE', state: 'RECALLED' });
    events.push({ t: 'FOUL', offender: adv.offender, victim: adv.victim, severity: adv.severity, outcome: 'FREE_KICK', restart: 'FREE_KICK', spot: { ...adv.restart.spot } });
    events.push({ t: 'WHISTLE', kind: 'FOUL' });
    if (card) applyCard(state, adv.offender, card, 'FOUL', cfg, events);
    startRestart(state, events, cfg, 'FREE_KICK', adv.victimTeam, { ...adv.restart.spot });
  }
  state.advantage = null;
}

export function refereeStep(state: MatchState, contacts: readonly ContactEvent[], cfg: SimConfig, events: SimEvent[]): void {
  resolveAdvantage(state, cfg, events); // tick down / resolve a held advantage first (works in any phase)
  if (state.phase !== 'PLAY') {
    brandishQueuedCards(state, cfg, events); // the ball is dead ⇒ brandish any cards queued in prior play (§7.6)
    moveReferee(state, cfg); // still trot toward play; no new fouls while the ball is dead
    return;
  }
  const ref = state.referee;

  // 1 · referee abuse (no rng): an aggressive action-driven contact ON the referee ⇒ instant red, free kick
  const abuseR = cfg.collision.CAPSULE_R * 2 + 0.1;
  for (const p of state.players) {
    if (p.sentOff || !AGGRESSIVE.has(p.action)) continue;
    if (Math.hypot(p.pos.x - ref.pos.x, p.pos.y - ref.pos.y) > abuseR) continue;
    const other: TeamIdx = p.team === 0 ? 1 : 0;
    events.push({ t: 'FOUL', offender: p.id, victim: p.id, severity: cfg.referee.PUNCH_SEV, outcome: 'FREE_KICK', restart: 'FREE_KICK', spot: { ...ref.pos } });
    events.push({ t: 'WHISTLE', kind: 'FOUL' });
    applyCard(state, p.id, 'red', 'REF_ABUSE', cfg, events);
    startRestart(state, events, cfg, 'FREE_KICK', other, { ...ref.pos });
    return; // the whistle stops play immediately — the ref felt it, no detection roll, no scan this tick
  }

  // 2 · foul candidates (ascending offender id, victim id tiebreak) — one detection roll each
  const cands: Candidate[] = [];
  for (const c of contacts) {
    const pa = state.players[c.a]!;
    const pb = state.players[c.b]!;
    if (pa.team === pb.team) continue; // opponents only
    // D99: judge the actions AS RECORDED AT CONTACT. `applyRagdolls` (5b) rewrites `action = 'RAGDOLL'` on
    // both participants of any impulse ≥ RAGDOLL_IMPULSE_MIN (10) contact — the tackler too — and that runs
    // BEFORE this step (7). Reading the live action therefore made AGGRESSIVE.has() false for exactly the
    // most violent challenges and dropped them here: a 4 m/s slide drew a yellow while an otherwise
    // identical 12 m/s slide drew nothing at all. ContactEvent carries aAction/bAction for this purpose.
    const aAction = c.aAction;
    const bAction = c.bAction;
    const aRank = actionRank(aAction, cfg);
    const bRank = actionRank(bAction, cfg);
    if (!AGGRESSIVE.has(aAction) && !AGGRESSIVE.has(bAction)) continue; // a pure duel isn't a foul candidate here
    const [offender, victim] = aRank >= bRank ? [c.a, c.b] : [c.b, c.a];
    const sev = foulSeverity(state, c, offender, victim, cfg).severity;
    const verdict = classifyFoul(sev, cfg);
    if (verdict === 'legal') continue;
    const spot = { x: (pa.pos.x + pb.pos.x) / 2, y: (pa.pos.y + pb.pos.y) / 2 };
    cands.push({ offender, victim, severity: sev, verdict, spot });
  }
  cands.sort((x, y) => x.offender - y.offender || x.victim - y.victim);

  const detected: Candidate[] = [];
  for (const cand of cands) {
    const clarity = refClarity(ref.pos, ref.viewDir, cand.spot, state, new Set<EntityId>([cand.offender, cand.victim]), cfg);
    const pd = pDetect(clarity, cand.severity, cfg);
    const roll = rngFloat(state); // one draw per candidate, in order (§7)
    if (roll >= pd) continue;
    // cried-wolf: a known diver's "foul" with poor clarity and sub-yellow severity is waved off (P18 cap)
    const rep = state.discipline.perPlayer[cand.victim]!.diveReputation;
    if (rep < cfg.referee.R_WOLF && clarity < cfg.referee.C_WOLF && cand.severity < cfg.referee.T_YELLOW) continue;
    detected.push(cand);
  }

  // 3 · multiplicity: the highest-severity detected foul claims the restart; the rest queue their cards
  if (detected.length > 0) {
    detected.sort((x, y) => y.severity - x.severity || x.offender - y.offender);
    const top = detected[0]!;
    const victimTeam = state.players[top.victim]!.team;
    const offenderTeam = state.players[top.offender]!.team;
    const penalty = spotInOwnBox(state, top.spot, offenderTeam);
    // every non-top detected candidate queues its card regardless of advantage — but ONE ACTION IS ONE
    // OFFENCE (D102). Without deduping by offender, a single slide overlapping two opponents produced two
    // candidates with the SAME offender: the top was carded now and the second queued, so the next stoppage
    // brandished it and the player had two yellows ⇒ sent off for one challenge. Multiplicity is about
    // several offenders in a tick, not one offender hitting several people.
    const booked = new Set<number>([top.offender]);
    for (let i = 1; i < detected.length; i++) {
      const cand = detected[i]!;
      if (booked.has(cand.offender)) continue;
      const card = cardFor(cand.verdict);
      if (card) {
        state.cardQueue.push({ entity: cand.offender, card: card === 'red' ? 'RED' : 'YELLOW', reason: 'FOUL' });
        booked.add(cand.offender);
      }
    }

    // advantage (§7.3): a non-penalty foul on the team currently in possession holds the whistle
    if (!penalty && possessorTeamOf(state) === victimTeam) {
      // a second foul during the window only replaces the record if it is more severe (§7.3)
      if (!state.advantage || top.severity > state.advantage.severity) {
        state.advantage = { offender: top.offender, victim: top.victim, victimTeam, severity: top.severity, ticksLeft: cfg.referee.ADV_WINDOW, restart: { kind: 'FREE_KICK', spot: { ...top.spot }, team: victimTeam, taker: null } };
        events.push({ t: 'ADVANTAGE', state: 'HELD' });
      }
    } else {
      state.advantage = null; // a fresh whistle supersedes any held advantage — no dangling double restart
      const outcome: FoulOutcome = penalty ? 'PENALTY' : 'FREE_KICK';
      events.push({ t: 'FOUL', offender: top.offender, victim: top.victim, severity: top.severity, outcome, restart: penalty ? 'PENALTY' : 'FREE_KICK', spot: { ...top.spot } });
      events.push({ t: 'WHISTLE', kind: 'FOUL' });
      // D55: a WHISTLED foul FELLS its victim — a foul must be VISIBLE. Knockback away from the
      // offender; the client's theatrical tumble does the drama, RAGDOLL runs its normal recovery
      // through the restart window. (Held advantage never fells — the victim rode the challenge.)
      {
        const vp = state.players[top.victim];
        const op = state.players[top.offender];
        if (vp && op && !vp.sentOff && vp.action !== 'RAGDOLL' && vp.action !== 'PRONE') {
          const dx = vp.pos.x - op.pos.x;
          const dy = vp.pos.y - op.pos.y;
          const d = Math.hypot(dx, dy) || 1;
          vp.action = 'RAGDOLL';
          vp.actionTicks = 0;
          vp.charge = null;
          vp.vel.x += (dx / d) * cfg.referee.FOUL_KNOCKBACK;
          vp.vel.y += (dy / d) * cfg.referee.FOUL_KNOCKBACK;
          events.push({ t: 'RAGDOLL', entity: top.victim, cause: 'FOUL' });
        }
      }
      const topCard = cardFor(top.verdict);
      if (topCard) applyCard(state, top.offender, topCard, 'FOUL', cfg, events);
      if (penalty) startPenalty(state, events, cfg, victimTeam);
      else startRestart(state, events, cfg, 'FREE_KICK', victimTeam, { ...top.spot });
    }
  }

  // 4 · dive judgments (§7.4), ascending diver id — deterministic, after the foul detection draws
  if (state.phase === 'PLAY') judgeDives(state, cfg, events);

  moveReferee(state, cfg, detected.length > 0); // §7.5 — after the detection draws + dives, per the rng order
}

/** Judge every player entering DIVE_FALL this tick (§7.4); the first non-PLAY_ON verdict stops play. */
function judgeDives(state: MatchState, cfg: SimConfig, events: SimEvent[]): void {
  const divers = state.players.filter((p) => p.action === 'DIVE_FALL' && p.actionTicks === 0).sort((a, b) => a.id - b.id);
  for (const p of divers) {
    const disc = state.discipline.perPlayer[p.id]!;
    disc.divesAttempted += 1;
    const j = judgeDive(state, p.id, cfg);
    events.push({ t: 'DIVE_JUDGED', entity: p.id, verdict: j.verdict });
    if (j.verdict === 'CAUGHT') {
      disc.divesCaught += 1;
      disc.diveReputation = Math.max(cfg.referee.DIVE_REP_MIN, disc.diveReputation - cfg.referee.DIVE_REP_PENALTY);
      const opp: TeamIdx = p.team === 0 ? 1 : 0;
      events.push({ t: 'WHISTLE', kind: 'FOUL' });
      applyCard(state, p.id, 'yellow', 'SIMULATION', cfg, events);
      startRestart(state, events, cfg, 'FREE_KICK', opp, { ...p.pos });
      return; // play stopped
    }
    if (j.verdict === 'AWARDED') {
      disc.divesWon += 1;
      const opp: TeamIdx = p.team === 0 ? 1 : 0;
      const penalty = spotInOwnBox(state, p.pos, opp); // the dive is in the opponents' defensive box
      if (penalty) startPenalty(state, events, cfg, p.team);
      else startRestart(state, events, cfg, 'FREE_KICK', p.team, { ...p.pos });
      return; // play stopped
    }
    // PLAY_ON: nothing further
  }
}
